import "@xyflow/react/dist/style.css";
import { Background, Controls, Node, NodeTypes, ReactFlow, ReactFlowProvider, useReactFlow } from "@xyflow/react";
import { ContextualTipsProvider } from "@babackapp/ui/components/contextual-tips";
import { VariablesDictionaryProvider } from "@babackapp/variables-registry";
import { useWorkflowsEditorConfig } from "./context/workflows-editor-config-context";
import { EditorNodeType, workflowNodesLibrary } from "./nodes";
import {
  useWorkflowForm,
  useWorkflowsEditorWorkflows,
  WorkflowsEditorWorkflowsProvider,
} from "./context/workflows-editor-workflows-context";
import { useUnsavedChanges } from "./hooks/use-unsaved-changes";
import { BottomBar } from "./components/bottom-bar";
import { RightSidebar } from "./components/right-sidebar";
import { WorkflowSelectionProvider } from "./context/workflow-selection-context";
import { WorkflowsListSidebar } from "./listing/workflows-list-sidebar";
import { CreateWorkflowModal } from "./listing/create-workflow-modal";
import { buildTriggerNode } from "./nodes/trigger/constants";
import type { TriggerNodeDataDto } from "@babackapp/private-api-client/types";
import { WorkflowTopBar } from "./components/workflow-top-bar";
import { WorkflowExecutionsList } from "./components/left-sidebar/left-sidebar-workflow-executions";
import useWorkflowData from "./hooks/use-workflow-data";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SelectedNode, SelectionProvider } from "./context/node-selection-context";
import { AddNodeProvider, useAddNodeContext } from "./context/add-node-context";
import { EditModeProvider } from "./context/edit-mode-context";
import { WorkflowCodeEditorSheet } from "./components/workflow-code-editor-sheet";
import { WorkflowGroupNode } from "./components/nodes/WorkflowGroupNode";
import { WorkflowSelectedGroupProvider } from "./context/workflow-selected-group-context";
import { Button } from "@babackapp/ui/components/button";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDefaultEditorVariables } from "./lib/variables";
import { useTriggerType } from "./hooks/use-trigger-type";
import { TrackingWorkflowsProvider } from "./tracking-provider";


export type WorkflowsEditorProps = {
  currentWorkflowId?: string | null;
  onWorkflowSelect?: (workflowId?: string) => void;
  devMode?: boolean;
  theme?: "dark" | "light";
};

// Build node types object from the registry
const editorNodeTypes = {
  ...Object.fromEntries(
    Object.entries(workflowNodesLibrary).map(([type, node]) => [
      type,
      node?.EditorComponent,
    ]),
  ),
  group: WorkflowGroupNode,
};

function EmptyState() {
  const { t } = useTranslation("trackoffice");
  const { workflows, addWorkflow, onWorkflowSelect } =
    useWorkflowsEditorWorkflows();
  const [createOpen, setCreateOpen] = useState(false);

  const handleCreate = (
    triggerType: TriggerNodeDataDto["triggerType"],
    config: TriggerNodeDataDto["config"],
  ) => {
    addWorkflow({ trigger: buildTriggerNode(triggerType, config) })
      .then((workflow) => onWorkflowSelect?.(workflow.id))
      .catch((error) => console.error("Failed to add workflow:", error));
  };

  return (
    <div className="flex items-center justify-center h-full w-full">
      <div className="flex flex-col items-center gap-6 max-w-md text-center p-8 rounded-lg border bg-card">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-semibold">
            {t("workflowsEditor.emptyState.title", "No workflows yet")}
          </h2>
          <p className="text-muted-foreground">
            {t(
              "workflowsEditor.emptyState.description",
              "Get started by creating your first workflow to automate notifications based on tracking status changes.",
            )}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} size="lg">
          <Plus className="h-5 w-5 mr-2" />
          {t(
            "workflowsEditor.emptyState.createButton",
            "Create your first workflow",
          )}
        </Button>
      </div>
      <CreateWorkflowModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        workflows={workflows}
        onCreate={handleCreate}
      />
    </div>
  );
}

function WorkflowsEditorInner({ theme }: { theme?: "dark" | "light" }) {
  const { t } = useTranslation("trackoffice");
  const { nodes, edges } = useWorkflowData();
  const form = useWorkflowForm();
  const isDirty = form.formState.isDirty;
  const defaultEditorVariables = useDefaultEditorVariables();
  const { getVariablesForTrigger, variableUniverse } =
    useWorkflowsEditorConfig();
  const triggerType = useTriggerType();
  const unavailableVariableIds = useMemo(() => {
    const availableIds: Set<string> = new Set(getVariablesForTrigger(triggerType));
    // Computed over the domain's full variable universe (not just the
    // displayed workflow variables): the set propagates into the embedded
    // template editor, which renders template-only variables too.
    return new Set(
      variableUniverse.filter(id => !availableIds.has(id)),
    );
  }, [triggerType, getVariablesForTrigger, variableUniverse]);
  const [selected, setSelectedState] = useState<SelectedNode | null>(null);
  const [executionsOpen, setExecutionsOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const setSelected = useCallback((newSelected: SelectedNode | null) => {
    setSelectedState(newSelected);
  }, []);
  const { clearAddNodeSource } = useAddNodeContext();
  const { fitView } = useReactFlow();
  const { workflows, currentWorkflow, consumeJustCreatedWorkflow } =
    useWorkflowsEditorWorkflows();
  const hasWorkflow = !!currentWorkflow;

  useUnsavedChanges(isDirty);

  // Reset transient UI state when the selected workflow changes.
  // A workflow that was just created opens directly in edit mode so the user
  // can add actions without an extra "Edit" click.
  useEffect(() => {
    const openInEditMode =
      !!currentWorkflow?.id && consumeJustCreatedWorkflow(currentWorkflow.id);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting local UI state on workflow switch
    setEditMode(openInEditMode);
    setSelected(null);
    setExecutionsOpen(false);
  }, [currentWorkflow?.id, setSelected, consumeJustCreatedWorkflow]);

  // Center the selected workflow when its nodes change
  useEffect(() => {
    if (!hasWorkflow || nodes.length === 0) return;
    const id = setTimeout(() => fitView({ duration: 300, padding: 0.2 }), 0);
    return () => clearTimeout(id);
  }, [hasWorkflow, nodes.length, fitView]);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (!editMode) return;
      setSelected({
        id: node.id,
        type: node.type as EditorNodeType,
        data: node.data,
      });
      clearAddNodeSource();
    },
    [clearAddNodeSource, editMode],
  );

  const handleUpdateNode = useCallback((nodeId: string, newData: unknown) => {
    // This is just a callback, the logic is handled using the form context
    void nodeId;
    void newData;
  }, []);

  const handlePaneClick = useCallback(() => {
    setSelected(null);
    clearAddNodeSource();
  }, [clearAddNodeSource]);

  if (workflows.length === 0) {
    return (
      <div className="flex h-full w-full relative flex-col">
        <EmptyState />
      </div>
    );
  }

  return (
    <VariablesDictionaryProvider variablesDefinition={defaultEditorVariables} unavailableVariableIds={unavailableVariableIds}>
      <EditModeProvider editMode={editMode}>
      <SelectionProvider
        selected={selected}
        setSelected={setSelected}
        onClearSelection={() => setSelected(null)}
      >
        <div className="flex h-full w-full relative flex-row">
          <WorkflowSelectionProvider>
            <WorkflowsListSidebar />
          </WorkflowSelectionProvider>
          <div className="flex min-h-0 flex-1 flex-col">
            {hasWorkflow ? (
              <>
                <WorkflowTopBar
                  editMode={editMode}
                  onToggleEdit={() => setEditMode((edit) => !edit)}
                  executionsOpen={executionsOpen}
                  onToggleExecutions={() => setExecutionsOpen((open) => !open)}
                />
                <div className="flex min-h-0 flex-1 flex-row">
                  <div className="relative flex-1">
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      nodeTypes={editorNodeTypes as NodeTypes}
                      onNodeClick={handleNodeClick}
                      onPaneClick={handlePaneClick}
                      connectOnClick={false}
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable={editMode}
                      colorMode={theme}
                    >
                      <Background />
                      <Controls />
                    </ReactFlow>
                    {(editMode || isDirty) && (
                      <div className="absolute bottom-8 left-0 right-0 pointer-events-none">
                        <div className="pointer-events-auto">
                          <BottomBar />
                        </div>
                      </div>
                    )}
                    {editMode && (
                      <div className="absolute top-2 right-2">
                        <RightSidebar onUpdateNode={handleUpdateNode} />
                      </div>
                    )}
                  </div>
                  {executionsOpen && (
                    <div className="w-80 shrink-0 border-l bg-background">
                      <WorkflowExecutionsList
                        onClose={() => setExecutionsOpen(false)}
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center p-8 text-center text-muted-foreground">
                {t(
                  "workflowsEditor.listing.selectPlaceholder",
                  "Select a workflow to preview it, or create a new one.",
                )}
              </div>
            )}
          </div>
          <WorkflowCodeEditorSheet />
        </div>
      </SelectionProvider>
      </EditModeProvider>
    </VariablesDictionaryProvider>
  );
}

/**
 * Router-agnostic editor core. Assumes a WorkflowsEditorRoutingProvider above
 * it (supplying shopId + navigation hooks). Shared by the tracking default
 * export below and the future return wrapper (baback-office host).
 */
export function WorkflowsEditorCore(props: WorkflowsEditorProps) {
  return (
    <ReactFlowProvider>
      <WorkflowSelectedGroupProvider>
        <WorkflowsEditorWorkflowsProvider currentWorkflowId={props.currentWorkflowId} onWorkflowSelect={props.onWorkflowSelect}>
          <ContextualTipsProvider>
            <AddNodeProvider>
              <WorkflowsEditorInner theme={props.theme} />
            </AddNodeProvider>
          </ContextualTipsProvider>
        </WorkflowsEditorWorkflowsProvider>
      </WorkflowSelectedGroupProvider>
    </ReactFlowProvider>
  );
}

// craftsman-ignore: TS002
export default function WorkflowsEditor(props: WorkflowsEditorProps) {
  return (
    <TrackingWorkflowsProvider>
      <WorkflowsEditorCore {...props} />
    </TrackingWorkflowsProvider>
  );
}
